import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  FlatActionList,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import {
  AnthropiciModels,
  GeminiModels,
  OpenAiDalleModels,
  OpenAiModels,
  ScreenContext,
} from "./common";
import React from "react";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Feather } from "@expo/vector-icons";
import { Colors } from "react-native-ui-lib";
import { MUTATION_UPDATE_LLM_STRATEGY } from "../../common";
import { AiStrategy } from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";

export interface UpdateLlmModelScreenProps
  extends StackScreenProps<any, "UpdateLlmModelScreen"> {}

export const UpdateLlmModelScreen = ({
  navigation,
  route,
}: UpdateLlmModelScreenProps) => {
  const [selectedValue, setSelectedValue] = React.useState<string | null>(null);
  const { t } = useTranslation("settings");
  const { data, setStrategy } = React.useContext(ScreenContext);
  const llmStrategy = data?.strategy;
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    updateAiStrategy: AiStrategy;
  }>(MUTATION_UPDATE_LLM_STRATEGY);

  const transitionEnded = useHasTransitionEnded(navigation);

  const handleOnSelect = (code: string) => {
    setSelectedValue(code);
  };
  const aiCode = llmStrategy?.config?.code;

  const handleOnSubmit = async () => {
    const variables = {
      id: llmStrategy?.id!,
      options: {
        code: aiCode!,
        arguments: [
          {
            name: "model",
            value: selectedValue!,
          },
        ],
      },
    };

    return m({
      variables: {
        input: variables,
      },
    })
      .then((result) => {
        if (result.data?.updateAiStrategy) {
          setStrategy(result.data.updateAiStrategy);
          flashMessagesContext.showMessage({
            message: "AI model updated successfully!",
            severity: "success",
          });
          navigation.goBack();
        }
      })
      .catch((error) => {
        console.error("Error creating LLM Strategy:", error);
      });
  };

  if (llmStrategy === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  let options: {
    label: string;
    value: string;
    description?: string;
  }[] = [];

  switch (aiCode) {
    case "openai":
      options = OpenAiModels;
      break;
    case "anthropic":
      options = AnthropiciModels;
      break;
    case "gemini":
      options = GeminiModels;
      break;
    case "openai_dalle":
      options = OpenAiDalleModels;
      break;
  }

  const footerMarkup = (
    <Button
      disabled={selectedValue === null}
      label={t("button.update")}
      backgroundColor={Colors.action}
      color="white"
      onPress={async () => await handleOnSubmit()}
    ></Button>
  );

  return (
    <Page
      title={t("title.select_ai_model")}
      secondaryTitle={t("title.select_ai_model_secondary")}
      footer={footerMarkup}
    >
      <View>
        <FlatActionList
          items={options.map((llmType) => ({
            key: llmType.value,
            title: llmType.label,
            subTitle: llmType.description,
            selected: llmType.value === selectedValue,
            onPress: () => {
              handleOnSelect(llmType.value);
            },
            componentRight: () => {
              return llmType.value === selectedValue ? (
                <Feather name="check" size={24} color={Colors.action} />
              ) : null;
            },
          }))}
        />
      </View>
    </Page>
  );
};
